<template>
  <div
    class="trending-block"
    :class="{ 'trending-block--mobile-padding': windowWidth < 1024 }"
    role="list"
    aria-labelledby="titleId"
  >
    <div class="trending-block__header-row">
      <h2
        :id="titleId"
        class="trending-block__title"
      >
        {{ mainTitle }}
      </h2>
      <nav
        class="trending-block__collections_desktop"
        role="tablist"
        :aria-label="$string('collections')"
      >
        <button
          v-for="(block, idx) of liquid.blocks"
          :key="block.blockTitle || idx"
          class="trending-block__tab"
          :class="{ selected: idx === selectedCollectionIdx }"
          role="tab"
          :aria-selected="idx === selectedCollectionIdx ? 'true' : 'false'"
          :aria-controls="`collection-panel-${idx}`"
          tabindex="0"
          :style="tabStyle(idx === selectedCollectionIdx)"
          type="button"
          @click="selectCollection(idx)"
        >
          {{ block.blockTitle || $string('general.title') }}
        </button>
      </nav>
    </div>
    <nav

      class="trending-block__collections_mobile"
      role="tablist"
      :aria-label="$string('collections')"
    >
      <button
        v-for="(block, idx) of liquid.blocks"
        :key="block.blockTitle || idx"
        class="trending-block__tab"
        :class="{ selected: idx === selectedCollectionIdx }"
        role="tab"
        :aria-selected="idx === selectedCollectionIdx ? 'true' : 'false'"
        :aria-controls="`collection-panel-${idx}`"
        tabindex="0"
        type="button"
        :style="tabStyle(idx === selectedCollectionIdx)"
        @click="selectCollection(idx)"
      >
        {{ block.blockTitle || $string('general.title') }}
      </button>
    </nav>
    <div class="trending-block__products-row">
      <button
        v-if="isMobile && canScrollLeft"
        class="trending-block__arrow trending-block__arrow--overlay left"
        :aria-label="$string('Scroll left to view more products on trending block')"
        type="button"
        tabindex="0"
        @click="scrollLeft"
      >
        <trending-button-left />
      </button>
      <transition
        name="fade"
        mode="out-in"
        @after-enter="onProductListTransitioned"
      >
        <div
          :id="`collection-panel-${selectedCollectionIdx}`"
          :key="selectedCollectionIdx"
          ref="productList"
          class="trending-block__product-list"
          :aria-labelledby="`collection-panel-${selectedCollectionIdx}`"
          role="tabpanel"
          aria-live="polite"
          tabindex="0"
        >
          <div
            v-for="(product, idx) of displayedProducts"
            :key="product.id || idx"
            class="trending-block__product"
          >
            <a
              class="trending-block__product-link"
              :href="`/products/${product.handle}`"
              :aria-label="productAriaLabel(product)"
              tabindex="0"
            >
              <!-- eslint-disable-next-line vue/no-restricted-html-elements -->
              <img
                class="trending-block__product-image"
                :src="getProductImage(product)"
                :alt="product.imageAlt"
                loading="lazy"
              >

            </a>

            <div class="trending-block__product-info">
              <span
                v-if="product.onSale"
                class="trending-block__sale-badge"
                :style="saleBadgeStyle"
              >
                {{ $string('product.on_sale') }}
              </span>
              <a
                class="trending-block__product-name"
                :href="`/products/${product.handle}`"
                tabindex="0"
              >
                {{
                  product.title.length > 20
                    ? product.title.slice(0, 20).toUpperCase() + '…'
                    : product.title.toUpperCase()
                }}
              </a>

              <product-prices
                class="trending-block__product_prices"
                :compare-at-price="product.compare_at_price"
                :price="product.price"
                :show-discount="showDiscount"
              />
            </div>
          </div>
        </div>
      </transition>
      <button
        v-if="isMobile && canScrollRight"
        class="trending-block__arrow trending-block__arrow--overlay right"
        :aria-label="$string('Scroll right to view more products on trending block')"
        type="button"
        tabindex="0"
        @click="scrollRight"
      >
        <trending-button-right />
      </button>
    </div>
  </div>
</template>

<script>
import ProductPrices from '~global/product-prices/product-prices'
import TrendingButtonLeft from '~icons/directional-navigation/arrow/trending_block_left.svg'
import TrendingButtonRight from '~icons/directional-navigation/arrow/trending_block_right.svg'
import { mapState } from 'vuex'
export default {
  name: 'TrendingBlock',
  components: {
    ProductPrices,
    TrendingButtonLeft,
    TrendingButtonRight,
  },
  props: {
    liquid: {
      type: Object,
      default: () => ({
        sectionId: '',
        blocks: [],
      }),
    },
  },
  data() {
    return {
      selectedCollectionIdx: 0,
      scrollPosition: 0,
      scrollKey: 0,
      titleId: 'trending-block-title',
      windowWidth: window.innerWidth,
    }
  },
  computed: {
    showDiscount() {
      return this.liquid.showDiscount || 'show'
    },
    canScrollLeft() {
      // eslint-disable-next-line no-unused-expressions
      this.scrollKey
      const el = this.$refs.productList
      if (!el) {
        return false
      }
      return Math.abs(el.scrollLeft) > 16
    },
    canScrollRight() {
      // eslint-disable-next-line no-unused-expressions
      this.scrollKey
      const el = this.$refs.productList
      if (!el) {
        return false
      }
      return el.scrollLeft + el.clientWidth < el.scrollWidth - 4
    },

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),
    mainTitle() {
      return (
        this.liquid.mainTitle ||
        (this.liquid.blocks[0] && this.liquid.blocks[0].blockTitle) ||
        this.$string('general.title')
      )
    },
    displayedProducts() {
      const block = this.liquid.blocks[this.selectedCollectionIdx]
      if (!block || !block.products) {
        return []
      }

      return block.products
        .filter(productItem => productItem && productItem.product)
        .map(productItem => ({
          ...productItem.product,
          customImage: productItem.customImage,
        }))
    },

    tabStyle() {
      return () => ({

      })
    },
  },
  watch: {
    displayedProducts() {
      this.$nextTick().then(() => {
        if (this.$refs.productList) {
          this.$refs.productList.removeEventListener('scroll', this.updateScrollArrows)
          this.$refs.productList.addEventListener('scroll', this.updateScrollArrows)
        }
        this.updateScrollArrows()
      })
    },
  },
  mounted() {
    if (this.$refs.productList) {
      this.$refs.productList.addEventListener('scroll', this.updateScrollArrows)
    }
    window.addEventListener('resize', this.handleResize)
    this.handleResize()
    this.$nextTick().then(this.updateScrollArrows)
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize)
    if (this.$refs.productList) {
      this.$refs.productList.removeEventListener('scroll', this.updateScrollArrows)
    }
  },
  methods: {
    onProductListTransitioned() {
      this.$nextTick().then(() => {
        if (this.$refs.productList) {
          this.$refs.productList.removeEventListener('scroll', this.updateScrollArrows)
          this.$refs.productList.addEventListener('scroll', this.updateScrollArrows)
        }
        this.updateScrollArrows()
      })
    },
    handleResize() {
      this.windowWidth = window.innerWidth
    },
    updateScrollArrows() {
      this.scrollKey++
      this.$forceUpdate()

    },
    scrollLeft() {
      const el = this.$refs.productList
      if (!el) {
        return
      }
      const card = el.querySelector('.trending-block__product-link')
      const scrollAmount = card ? card.offsetWidth + 16 : 220
      el.scrollBy({ left: -scrollAmount, behavior: 'smooth' })
      setTimeout(this.updateScrollArrows, 350)

    },
    scrollRight() {
      const el = this.$refs.productList
      if (!el) {
        return
      }
      const card = el.querySelector('.trending-block__product-link')
      const scrollAmount = card ? card.offsetWidth + 16 : 220
      el.scrollBy({ left: scrollAmount, behavior: 'smooth' })
      setTimeout(this.updateScrollArrows, 350)

    },

    getProductImage(product) {
      const block = this.liquid.blocks[this.selectedCollectionIdx]
      if (product.customImage) {
        return product.customImage
      }
      if (
        block &&
        block.useSecondImage &&
        product.media &&
        product.media.length > 1
      ) {
        return product.media[1].src || product.media[1]
      }
      if (product.media && product.media.length) {
        return product.media[0].src || product.media[0]
      }
      return ''
    },

    selectCollection(idx) {
      this.selectedCollectionIdx = idx
      this.scrollPosition = 0
    },

    productAriaLabel(product) {
      let label = `${product.title}, ${product.price}`
      if (product.onSale) {
        label += `, On sale: Was ${product.compareAtPrice}, now ${product.price}`
      }
      return label
    },
  },
}
</script>

<style lang="scss">
@import './trending-block';
</style>
